import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useBookingDialog } from "@/contexts/BookingDialogContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { value: "uae", label: "United Arab Emirates", cities: ["Dubai", "Abu Dhabi", "Sharjah", "Ras Al Khaimah"] },
  { value: "saudi-arabia", label: "Saudi Arabia", cities: ["Riyadh", "Jeddah", "Dammam", "Khobar", "Mecca", "Medina"] },
  { value: "qatar", label: "Qatar", cities: ["Doha", "Al Wakrah"] },
  { value: "kuwait", label: "Kuwait", cities: ["Kuwait City"] },
  { value: "bahrain", label: "Bahrain", cities: ["Manama"] },
  { value: "oman", label: "Oman", cities: ["Muscat", "Salalah"] },
  { value: "egypt", label: "Egypt", cities: ["Cairo", "Alexandria", "Giza"] },
  { value: "jordan", label: "Jordan", cities: ["Amman", "Aqaba"] },
  { value: "lebanon", label: "Lebanon", cities: ["Beirut"] },
  { value: "morocco", label: "Morocco", cities: ["Casablanca", "Marrakech", "Rabat"] },
  { value: "turkey", label: "Türkiye", cities: ["Istanbul", "Ankara", "Antalya"] },
  { value: "pakistan", label: "Pakistan", cities: ["Karachi", "Lahore", "Islamabad"] },
  { value: "other", label: "Other (mention in notes)", cities: [] },
];

const BUSINESS_TYPES = [
  "Boutique hotel",
  "Resort",
  "Hostel",
  "Restaurant — fine dining",
  "Restaurant — casual",
  "Café / Coffee shop",
  "Bakery / Patisserie",
  "Bar / Lounge",
  "Aesthetic / Beauty clinic",
  "Dental clinic",
  "Wellness / Spa",
  "Salon / Hairdresser",
  "Barber",
  "Fitness studio / Gym",
  "Yoga / Pilates studio",
  "Boutique retail",
  "Concept store",
  "Jewellery / Watches",
  "Fashion flagship",
  "Bookstore",
  "Florist",
  "Co-working space",
  "Event venue",
  "Gallery / Museum",
  "Real estate showroom",
  "Auto showroom",
  "Travel / Tour operator",
  "Other",
];

const CONTRACT_TIERS = [
  { value: "visit", label: "Visit only" },
  { value: "shoot", label: "Shoot only" },
  { value: "visit-shoot", label: "Visit + Shoot" },
  { value: "full", label: "Visit + Shoot + Digital sponsor" },
  { value: "advise", label: "Not sure — advise me" },
];

const CREATOR_SIZES = [
  { value: "nano", label: "Nano (1K – 10K)" },
  { value: "micro", label: "Micro (10K – 100K)" },
  { value: "mid", label: "Mid-tier (100K – 500K)" },
  { value: "macro", label: "Macro (500K – 1M)" },
  { value: "celebrity", label: "Celebrity (1M+)" },
  { value: "mix", label: "Mix / open to suggestions" },
  { value: "unsure", label: "Not sure yet" },
];

const initial = {
  name: "",
  brand_name: "",
  email: "",
  phone: "",
  country: "",
  city: "",
  business_type: "",
  contract_tier: "",
  creator_size: "",
  preferred_date: "",
};

export default function BookingDialog() {
  const { open, setOpen, defaultTier } = useBookingDialog();
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | loading | done

  // Reset form + status whenever the dialog opens (handles both programmatic
  // and user-driven opens; Radix onOpenChange only fires for user-driven).
  useEffect(() => {
    if (open) {
      setForm({ ...initial, contract_tier: defaultTier || "" });
      setStatus("idle");
    }
  }, [open, defaultTier]);

  const update = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.value,
      ...(key === "country" ? { city: "" } : {}),
    }));

  const selectedCountry = COUNTRIES.find((c) => c.value === form.country);
  const cityOptions = selectedCountry?.cities || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!form.email || !form.email.includes("@"))
      return toast.error("Please enter a valid email.");
    if (!form.country) return toast.error("Please choose a country.");
    if (!form.business_type) return toast.error("Please choose a business type.");

    setStatus("loading");
    try {
      const message = [
        form.contract_tier
          ? `Contract preference: ${
              CONTRACT_TIERS.find((c) => c.value === form.contract_tier)?.label
            }`
          : null,
      ]
        .filter(Boolean)
        .join("\n");

      await axios.post(`${API}/consultations`, {
        name: form.name,
        brand_name: form.brand_name || null,
        email: form.email,
        phone: form.phone || null,
        country: form.country,
        city: form.city || null,
        business_type: form.business_type,
        creator_size: form.creator_size || null,
        preferred_date: form.preferred_date || null,
        message: message || null,
      });
      setStatus("done");
      toast.success("Consultation request received.");
    } catch (err) {
      console.error(err);
      setStatus("idle");
      toast.error("Something went wrong. Try again in a moment.");
    }
  };

  const fieldCls =
    "h-11 w-full px-3.5 rounded-xl bg-white border border-[#B1B7AB]/55 text-[#0D3A35] text-sm outline-none focus:border-[#0D3A35] transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-white border-[#B1B7AB]/50 max-w-2xl sm:max-w-2xl p-0 overflow-hidden gap-0 max-h-[92vh] flex flex-col"
      >
        {status === "done" ? (
          <div className="p-10 text-center" data-testid="booking-dialog-success">
            <DialogHeader className="sr-only">
              <DialogTitle>Consultation request received</DialogTitle>
              <DialogDescription>
                Your consultation request has been submitted successfully.
              </DialogDescription>
            </DialogHeader>
            <span className="inline-flex w-14 h-14 rounded-full bg-[#0D3A35] text-white items-center justify-center">
              <Check className="w-6 h-6" strokeWidth={2.5} />
            </span>
            <h3 className="font-serif text-3xl text-[#0D3A35] mt-6">
              Request received.
            </h3>
            <p className="mt-3 text-sm text-[#276152] max-w-md mx-auto">
              Our team will review your brief and reach out within 1 business
              day to schedule your consultation.
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="booking-dialog-close-success"
              className="mt-8 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#0D3A35] text-white text-sm font-medium hover:bg-[#276152] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-8 pt-8 pb-4 text-left">
              <DialogTitle className="font-serif text-3xl text-[#0D3A35] font-normal">
                Book a consultation
              </DialogTitle>
              <DialogDescription className="text-sm text-[#276152]">
                Tell us about your venue. We&apos;ll match you with the right
                creators and contract.
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="px-8 pb-8 overflow-y-auto"
            >
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                    Your name
                  </label>
                  <input
                    data-testid="booking-name"
                    type="text"
                    required
                    placeholder="Full name"
                    value={form.name}
                    onChange={update("name")}
                    className={fieldCls + " mt-1.5"}
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                    Brand / venue
                  </label>
                  <input
                    data-testid="booking-brand"
                    type="text"
                    placeholder="Brand name"
                    value={form.brand_name}
                    onChange={update("brand_name")}
                    className={fieldCls + " mt-1.5"}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                    Email
                  </label>
                  <input
                    data-testid="booking-email"
                    type="email"
                    required
                    placeholder="you@brand.com"
                    value={form.email}
                    onChange={update("email")}
                    className={fieldCls + " mt-1.5"}
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                    Phone (optional)
                  </label>
                  <input
                    data-testid="booking-phone"
                    type="tel"
                    placeholder="+971 ..."
                    value={form.phone}
                    onChange={update("phone")}
                    className={fieldCls + " mt-1.5"}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                    Country
                  </label>
                  <select
                    data-testid="booking-country"
                    required
                    value={form.country}
                    onChange={update("country")}
                    className={fieldCls + " mt-1.5"}
                  >
                    <option value="" disabled>
                      Select country
                    </option>
                    {COUNTRIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                    City
                  </label>
                  <select
                    data-testid="booking-city"
                    value={form.city}
                    onChange={update("city")}
                    disabled={!form.country || cityOptions.length === 0}
                    className={fieldCls + " mt-1.5"}
                  >
                    <option value="">
                      {!form.country
                        ? "Choose country first"
                        : cityOptions.length === 0
                        ? "—"
                        : "Select city"}
                    </option>
                    {cityOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                    {form.country && cityOptions.length > 0 && (
                      <option value="Other">Other</option>
                    )}
                  </select>
                </div>
              </div>

              <div className="mt-3">
                <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                  Business type
                </label>
                <select
                  data-testid="booking-business-type"
                  required
                  value={form.business_type}
                  onChange={update("business_type")}
                  className={fieldCls + " mt-1.5"}
                >
                  <option value="" disabled>
                    Select business type
                  </option>
                  {BUSINESS_TYPES.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                    Contract preference
                  </label>
                  <select
                    data-testid="booking-contract-tier"
                    value={form.contract_tier}
                    onChange={update("contract_tier")}
                    className={fieldCls + " mt-1.5"}
                  >
                    <option value="">Any / advise me</option>
                    {CONTRACT_TIERS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                    Creator size
                  </label>
                  <select
                    data-testid="booking-creator-size"
                    value={form.creator_size}
                    onChange={update("creator_size")}
                    className={fieldCls + " mt-1.5"}
                  >
                    <option value="">No preference</option>
                    {CREATOR_SIZES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-3">
                <label className="text-[11px] uppercase tracking-[0.15em] text-[#276152] font-semibold">
                  Preferred shoot date (optional)
                </label>
                <input
                  data-testid="booking-date"
                  type="date"
                  value={form.preferred_date}
                  onChange={update("preferred_date")}
                  className={fieldCls + " mt-1.5"}
                />
              </div>

              <button
                type="submit"
                data-testid="booking-submit"
                disabled={status === "loading"}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl text-sm font-medium transition-all bg-[#0D3A35] text-white hover:bg-[#276152] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status === "loading" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Submit request
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
